import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthUser } from '@/types/auth';
import { tokenStorage } from '@/services/tokenStorage';
import { authService } from '@/features/auth/services/authService';
import { queryClient } from '@/queryClient';
import { connectTaskSocket, disconnectTaskSocket } from '@/services/taskRealtime';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  setSession: (user: AuthUser) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => tokenStorage.getUser());

  // Open task socket as soon as a session exists (independent of route/hooks).
  useEffect(() => {
    if (user && tokenStorage.getAccessToken()) {
      connectTaskSocket();
      return;
    }
    if (!tokenStorage.getAccessToken()) {
      disconnectTaskSocket();
    }
  }, [user]);

  const setSession = useCallback((nextUser: AuthUser) => {
    setUser(nextUser);
    const accessToken = tokenStorage.getAccessToken();
    const refreshToken = tokenStorage.getRefreshToken();
    if (accessToken && refreshToken) {
      tokenStorage.setTokens({ accessToken, refreshToken, user: nextUser });
    }
    connectTaskSocket();
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      disconnectTaskSocket();
      tokenStorage.clear();
      setUser(null);
      queryClient.clear();
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user && tokenStorage.getAccessToken()),
      setSession,
      logout,
    }),
    [user, setSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
